import { useEffect, useRef } from 'react';
import { useReactFlow, useStore as useRFStore } from '@xyflow/react';

export interface FitViewOnLoadProps {
  /** When true, fits the view once nodes are initialized. Idempotent — fires at most once per mount. */
  enabled: boolean;
  /** Padding fraction passed through to fitView. Defaults to 0.1 (10%). */
  padding?: number;
}

/**
 * Mounts inside <ReactFlow> and calls `fitView` once after the viewport has
 * real dimensions, the panZoom instance is ready, and there is at least one
 * node in the graph.
 *
 * Why we dropped `useNodesInitialized`:
 * `nodesInitialized` requires every node to have `handleBounds` populated,
 * which depends on `updateNodeInternals` running after the node DOM elements
 * are painted. In read-only canvases rendered inside a Portal (e.g. the entity
 * hover card), this internal bookkeeping never fires — RF skips handle-bounds
 * measurement for read-only nodes — so `nodesInitialized` stays `false`
 * forever even when nodes are visually rendered.
 *
 * Instead we subscribe to `nodeLookup.size` directly: once the RF store has at
 * least one node AND the viewport has real dimensions AND panZoom is ready, we
 * schedule `fitView` on the next animation frame to ensure the node DOM
 * elements have been painted before RF tries to read their bounding rects.
 */
export function FitViewOnLoad({ enabled, padding = 0.1 }: FitViewOnLoadProps) {
  const { fitView } = useReactFlow();
  const viewportWidth = useRFStore((s) => s.width);
  const viewportHeight = useRFStore((s) => s.height);
  const hasPanZoom = useRFStore((s) => s.panZoom !== null);
  const nodeCount = useRFStore((s) => s.nodeLookup.size);
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (viewportWidth === 0 || viewportHeight === 0) return;
    if (!hasPanZoom) return;
    if (nodeCount === 0) return;
    if (fittedRef.current) return;
    fittedRef.current = true;
    // rAF: give the browser one frame to paint the node DOM elements so RF's
    // internal getBoundingClientRect calls inside fitView return real sizes.
    requestAnimationFrame(() => {
      fitView({ padding });
    });
  }, [enabled, viewportWidth, viewportHeight, hasPanZoom, nodeCount, fitView, padding]);

  return null;
}
